import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { MoreVertical, AlertCircle, CheckCircle, Clock, Zap, XCircle, Hourglass, Wand } from 'lucide-react';
import type { LucideProps } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

const taskCardVariants = cva('w-full transition-all', {
  variants: {
    status: {
      default: 'border-border',
      urgent: 'border-destructive bg-destructive/10',
      error: 'border-destructive bg-destructive/10',
      manual: 'border-warning bg-warning/10',
      progress: 'border-primary bg-primary/10',
      pending: 'border-yellow-500 bg-yellow-500/10',
      complete: 'border-green-500 bg-green-500/10',
    },
    size: {
      default: 'max-w-md',
      sm: 'max-w-sm',
    },
  },
  defaultVariants: {
    status: 'default',
    size: 'default',
  },
});

type Status = VariantProps<typeof taskCardVariants>['status'];

interface StatusIndicatorProps {
  status?: Status;
  time?: string;
}

const statusIndicators: Record<Exclude<Status, null | undefined>, React.FC<StatusIndicatorProps>> = {
    urgent: () => <Badge variant="destructive"><AlertCircle className="mr-1 h-3 w-3" />Urgent</Badge>,
    error: () => <Badge variant="destructive"><XCircle className="mr-1 h-3 w-3" />Error</Badge>,
    pending: () => <Badge variant="outline" className="border-yellow-500 text-yellow-600"><Hourglass className="mr-1 h-3 w-3" />Pending</Badge>,
    complete: () => <Badge variant="outline" className="border-green-500 text-green-600"><CheckCircle className="mr-1 h-3 w-3" />Completed</Badge>,
    progress: ({ time }) => <Badge variant="outline"><Zap className="mr-1 h-3 w-3" />Saving {time}</Badge>,
    default: ({ time }) => <div className="text-sm text-muted-foreground flex items-center"><Clock className="mr-1.5 h-4 w-4" />{time}</div>,
    manual: () => null, // Manual has its own section
};

const StatusIndicator = React.memo<StatusIndicatorProps>(({ status, time }) => {
  const IndicatorComponent = status ? statusIndicators[status] : statusIndicators.default;
  return <IndicatorComponent time={time} />;
});
StatusIndicator.displayName = 'StatusIndicator';


interface InfoRowProps {
    label: string;
    value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <div className="flex justify-between items-center text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-foreground text-right">{value}</span>
  </div>
);
InfoRow.displayName = 'InfoRow';

interface Action {
    label: string;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
    onClick?: () => void;
    icon?: React.ComponentType<LucideProps>;
}

export interface TaskCardProps extends VariantProps<typeof taskCardVariants> {
  className?: string;
  patient: { name: string };
  task: { description: string };
  provider: { name: string };
  time?: string;
  progress?: number;
  actions?: Action[];
  manualReview?: string;
  type?: 'pixie' | 'manual';
}

const TaskCard = React.forwardRef<HTMLDivElement, TaskCardProps>(
  ({ className, status, size, patient, task, provider, time, progress, actions, manualReview, type, ...props }, ref) => {
    return (
      <Card ref={ref} className={cn(taskCardVariants({ status, size }), 'p-4 rounded-2xl shadow-lg', className)} {...props}>
        {/* Header Section */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            {type === 'pixie' && (
              <div className="p-2 bg-primary/10 rounded-full">
                <Wand className="h-5 w-5 text-primary" />
              </div>
            )}
            <div className="font-semibold text-foreground">{patient.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusIndicator status={status} time={time} />
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content Section */}
        <CardContent className="p-0 grid gap-2 mb-4">
          <InfoRow label="Task" value={task.description} />
          <InfoRow label="Provider" value={provider.name} />
          {status === 'progress' && progress !== undefined && (
            <div className="flex items-center gap-4 pt-2">
              <Progress value={progress} className="w-full" />
              <span className="text-sm font-semibold text-primary">{progress}%</span>
            </div>
          )}
        </CardContent>

        {/* Manual Review Section */}
        {manualReview && (
          <div className="border-l-4 border-destructive bg-destructive/10 p-3 rounded-r-lg mb-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-destructive mr-2 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-destructive-foreground">Manual review needed</p>
                <p className="text-destructive-foreground/90">{manualReview}</p>
              </div>
            </div>
          </div>
        )}

        {/* Footer Section */}
        {actions && actions.length > 0 && (
          <CardFooter className="p-0 flex justify-end space-x-2">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <Button key={action.label} variant={action.variant || 'default'} size="sm" onClick={action.onClick}>
                  {Icon && <Icon className="mr-2 h-4 w-4" />}
                  {action.label}
                </Button>
              );
            })}
          </CardFooter>
        )}
      </Card>
    );
  }
);

TaskCard.displayName = 'TaskCard';

export { TaskCard, taskCardVariants };
